import { atom, useAtom } from 'jotai'
import { useMemo } from 'react'
import { useAudioSettingsState } from '~/atoms/AudioSettingsState'
import { TransportConnectionState } from '~/hooks/usePhone'
import { useGetPbxLineExtensions } from '~/queries/extensionQueries'
import Logger from '~/services/Logger'
import { SpeedDialState } from '~/types/WebphoneTypes'
import { PresenceState } from '~/utils/xml'

import { RegistererState } from '@mangovoice/sip'
import { ExtensionPreview, PhoneCall, PhoneCallStatus } from '@mangovoice/types'

import { Extension, ParkingLot, useSettingsState } from './SettingsState'

const logger = new Logger('WebphoneState')

export enum PhoneStatus {
  INITIAL = 'INITIAL',
  READY = 'READY',
  CALL_CONNECTING = 'CALL_CONNECTING',
  RINGING = 'RINGING',
  CALL_ACTIVE = 'CALL_ACTIVE',
  TRANSPORT_DISCONNECTED = 'TRANSPORT_DISCONNECTED',
  ERROR = 'ERROR',
  MIC_DENIED = 'MIC_DENIED',
}

export enum MicrophonePermissionStatus {
  GRANTED = 'GRANTED',
  DENIED = 'DENIED',
  PROMPT = 'PROMPT',
  UNKNOWN = 'UNKNOWN',
}

export type TrackedParkingLot = ParkingLot & {
  presence?: PresenceState
}

export type TrackedExtension = Extension & {
  presence?: PresenceState
}

export type Conference = {
  callUuids: string[]
  startTime: Date
  isOnHold: boolean
  isMuted: boolean
}

const callListAtom = atom<Map<string, PhoneCall>>(new Map<string, PhoneCall>())
const conferenceAtom = atom<Conference | undefined>(undefined)
const activeCallUuidAtom = atom<string | null>(null)
const invitationUuidAtom = atom<string | null>(null)

const audioInputMediaStreamAtom = atom<MediaStream | null>(null)

const parkingPresenceAtom = atom<Map<number, PresenceState>>(
  new Map<number, PresenceState>(),
)

const extensionPresenceAtom = atom<Map<number, PresenceState>>(
  new Map<number, PresenceState>(),
)

export interface PresenceUpdate {
  id: number
  presenceState: PresenceState
}

export type ConferenceStateResponse = (
  | { conferencedCalls: PhoneCall[]; startTime: Date }
  | { conferencedCalls: undefined; startTime: undefined }
) & {
  isMuted: boolean
  isOnHold: boolean
}

type ConferenceResponse = ConferenceStateResponse & {
  addCalls: (callUuids: string[]) => void
  removeCalls: (callUuids: string[]) => void
  end: () => void
  setMute: (isMuted: boolean) => void
  setHold: (isOnHold: boolean) => void
}

export interface UseWebphoneStateResponse {
  phoneStatus: PhoneStatus
  phoneCalls: {
    active: {
      activeCall: PhoneCall | undefined
      activeCallUuid: string | null
      setActiveCallUuid: (callUuid: string | null) => void
    }
    conference: ConferenceResponse
    list: {
      callList: Map<string, PhoneCall>
      upsertCall: (call: PhoneCall) => void
      removeCall: (callUuid: string) => void
    }
    invite: {
      pendingInvitation: PhoneCall | undefined
      pendingInvitationUuid: string | null
      setPendingInvitationUuid: (callUuid: string | null) => void
    }
  }
  pbxExtensionsList: ExtensionPreview[]
  trackedExtensions: { list: Extension[]; withPresence: TrackedExtension[] }
  updateTrackedExtension: (update: PresenceUpdate) => void
  trackedParkingLots: { list: ParkingLot[]; withPresence: TrackedParkingLot[] }
  updateTrackedParkingLot: (update: PresenceUpdate) => void
  audio: {
    output: {
      devices: MediaDeviceInfo[]
      preference: [string, (deviceId: string) => void]
      setAudioOutputStream: (remoteStream: MediaStream) => void
    }
    input: {
      devices: MediaDeviceInfo[]
      preference: [string, (deviceId: string) => void]
      getAudioInputStream: () => Promise<MediaStream>
    }
  }
  speedDial: SpeedDialState
}

export const useWebphoneState = ({
  pbxId,
  registrationState,
  transportConnectionState,
}: {
  pbxId: number
  registrationState: RegistererState
  transportConnectionState: TransportConnectionState
}): UseWebphoneStateResponse => {
  const [activeCallUuid, setActiveCallUuid] = useAtom(activeCallUuidAtom)
  const [callList, setCallList] = useAtom(callListAtom)
  const [conference, setConference] = useAtom(conferenceAtom)
  const [pendingInvitationUuid, setPendingInvitationUuid] =
    useAtom(invitationUuidAtom)
  const [audioInputStream, setAudioInputStream] = useAtom(
    audioInputMediaStreamAtom,
  )
  const [parkingPresence, setParkingPresence] = useAtom(parkingPresenceAtom)
  const [extensionPresence, setExtensionPresence] = useAtom(
    extensionPresenceAtom,
  )

  const {
    settings: {
      pbx: { parkingLots: customParkingLots, extensions },
      speedDial,
    },
  } = useSettingsState(pbxId)

  const trackedParkingLots = useMemo(() => {
    return (
      customParkingLots?.map((parkingLot) => {
        const presence = parkingPresence.get(parkingLot.id)
        return { ...parkingLot, presence } as TrackedParkingLot
      }) ?? []
    )
  }, [customParkingLots, parkingPresence])

  const { data: allExtensions } = useGetPbxLineExtensions({
    pbxId,
    options: { gcTime: 60 * 60 * 1000 },
  })

  const trackedExtensions = useMemo(() => {
    return (
      extensions?.map((extension) => {
        const presence = extensionPresence.get(extension.id)
        return { ...extension, presence } as TrackedExtension
      }) ?? []
    )
  }, [extensions, extensionPresence])

  const {
    permission: microphonePermission,
    output: {
      devices: outputs,
      preference: [selectedOutputDeviceId, setSelectedOutputDeviceId],
    },
    input: {
      devices: inputs,
      preference: [selectedInputDeviceId, setSelectedInputDeviceId],
    },
  } = useAudioSettingsState()

  const getPhoneStatus = (
    registration: RegistererState,
    microphonePermissions: MicrophonePermissionStatus,
    activeCallStatus: PhoneCallStatus | null,
  ): PhoneStatus => {
    if (registration === RegistererState.Initial) {
      return PhoneStatus.INITIAL
    }
    if (transportConnectionState === TransportConnectionState.DISCONNECTED) {
      return PhoneStatus.TRANSPORT_DISCONNECTED
    }
    if (
      registration === RegistererState.Unregistered ||
      registration === RegistererState.Terminated
    ) {
      return PhoneStatus.ERROR
    }
    if (microphonePermissions === MicrophonePermissionStatus.DENIED) {
      return PhoneStatus.MIC_DENIED
    }
    if (activeCallStatus === PhoneCallStatus.INITIAL) {
      return PhoneStatus.CALL_CONNECTING
    }
    if (activeCallStatus === PhoneCallStatus.RINGING) {
      return PhoneStatus.RINGING
    }
    if (activeCallStatus === PhoneCallStatus.CONNECTED) {
      return PhoneStatus.CALL_ACTIVE
    }
    return PhoneStatus.READY
  }

  let microphonePermissionStatus: MicrophonePermissionStatus
  switch (microphonePermission) {
    case 'granted':
      microphonePermissionStatus = MicrophonePermissionStatus.GRANTED
      break
    case 'denied':
      microphonePermissionStatus = MicrophonePermissionStatus.DENIED
      break
    case 'prompt':
      microphonePermissionStatus = MicrophonePermissionStatus.PROMPT
      break
    default:
      microphonePermissionStatus = MicrophonePermissionStatus.UNKNOWN
      break
  }

  const cleanupAudioInput = () => {
    if (audioInputStream) {
      logger.debug(
        `Cleaning up audio input stream, status ${audioInputStream.active}`,
      )
      audioInputStream.getTracks().forEach((track) => track.stop())
      logger.debug(
        `Audio input stream stopped, status ${audioInputStream.active}`,
      )
      setAudioInputStream(null)
    }
  }
  const getAudioInputStream = async (): Promise<MediaStream> => {
    cleanupAudioInput()

    logger.debug('Connecting new audio input stream')
    const inputStream = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: { exact: selectedInputDeviceId } },
      video: false,
    })
    setAudioInputStream(inputStream)

    return inputStream
  }
  const setAudioOutputStream = (remoteStream: MediaStream) => {
    const audioElement = document.getElementById(
      'callAudio',
    ) as HTMLAudioElement
    audioElement.srcObject = remoteStream
  }

  const activeCall = callList.get(activeCallUuid ?? '')
  const pendingInvitation = callList.get(pendingInvitationUuid ?? '')
  const conferencedCalls = useMemo(
    () =>
      conference?.callUuids
        .map((callUuid) => callList.get(callUuid))
        .filter((call) => call !== undefined) as PhoneCall[] | undefined,
    [conference, conference?.callUuids?.length, callList],
  )

  const phoneStatus = getPhoneStatus(
    registrationState,
    microphonePermissionStatus,
    activeCall?.status ?? null,
  )

  const upsertCall = (call: PhoneCall) => {
    setCallList((list) => {
      const newCallList = new Map(list)
      newCallList.set(call.callUuid, call)
      return newCallList
    })
  }

  const removeCall = (callUuid: string) => {
    setCallList((list) => {
      const newCallList = new Map(list)
      newCallList.delete(callUuid)
      return newCallList
    })
    setConference((conference) => {
      if (conference) {
        const newList = conference.callUuids.filter((uuid) => uuid !== callUuid)
        if (newList.length === 0) {
          return undefined
        }
        conference.callUuids = newList
        return conference
      }
      return conference
    })
  }

  const addCallsToConference = (callUuids: string[]) => {
    if (callUuids.length === 0) {
      return
    }
    setConference((conference) => {
      if (conference) {
        conference.callUuids = [...conference.callUuids, ...callUuids]
        return conference
      }
      return {
        callUuids,
        isMuted: false,
        isOnHold: false,
        startTime: new Date(),
      }
    })
  }
  const removeCallsFromConference = (callUuids: string[]) => {
    setConference((conference) => {
      if (conference) {
        const newList = conference.callUuids.filter(
          (uuid) => !callUuids.includes(uuid),
        )
        if (newList.length === 0) {
          return undefined
        }
        conference.callUuids = newList
        return conference
      }
      return conference
    })
  }
  const endConference = () => {
    setConference(undefined)
  }
  const setConferenceMute = (isMuted: boolean) => {
    setConference((conference) => {
      if (conference) {
        conference.isMuted = isMuted
        return conference
      }
      return conference
    })
  }
  const setConferenceHold = (isOnHold: boolean) => {
    setConference((conference) => {
      if (conference) {
        conference.isOnHold = isOnHold
        return conference
      }
      return conference
    })
  }

  const updateTrackedParkingLot = ({ id, presenceState }: PresenceUpdate) => {
    logger.debug(`SUBSCRIBE: parking update for ${id} with ${presenceState}`)
    setParkingPresence((presence) => {
      const newPresence = new Map(presence)
      newPresence.set(id, presenceState)
      return newPresence
    })
  }
  const updateTrackedExtension = ({ id, presenceState }: PresenceUpdate) => {
    logger.debug(`SUBSCRIBE: extension update for ${id} with ${presenceState}`)
    setExtensionPresence((presence) => {
      const newPresence = new Map(presence)
      newPresence.set(id, presenceState)
      return newPresence
    })
  }

  const conferenceCallReturn =
    conference && conferencedCalls
      ? { conferencedCalls, startTime: conference.startTime }
      : { conferencedCalls: undefined, startTime: undefined }

  return {
    phoneStatus,
    phoneCalls: {
      list: { callList, upsertCall, removeCall },
      active: { activeCall, activeCallUuid, setActiveCallUuid },
      conference: {
        ...conferenceCallReturn,
        isMuted: conference?.isMuted ?? false,
        isOnHold: conference?.isOnHold ?? false,
        addCalls: addCallsToConference,
        removeCalls: removeCallsFromConference,
        end: endConference,
        setMute: setConferenceMute,
        setHold: setConferenceHold,
      },
      invite: {
        pendingInvitation,
        pendingInvitationUuid,
        setPendingInvitationUuid,
      },
    },
    trackedParkingLots: {
      list: customParkingLots ?? [],
      withPresence: trackedParkingLots,
    },
    updateTrackedParkingLot,
    trackedExtensions: {
      list: extensions ?? [],
      withPresence: trackedExtensions,
    },
    updateTrackedExtension,
    pbxExtensionsList: allExtensions?.results ?? [],
    audio: {
      output: {
        devices: outputs,
        preference: [selectedOutputDeviceId, setSelectedOutputDeviceId],
        setAudioOutputStream,
      },
      input: {
        devices: inputs,
        preference: [selectedInputDeviceId, setSelectedInputDeviceId],
        getAudioInputStream,
      },
    },
    speedDial,
  }
}
